import { People as CustomersIcon } from "@mui/icons-material";
import { Box, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminDialog } from "@/components/common";
import { Customer, CustomerService } from "@/services/features/CustomerService";

interface CustomerEditDialogProps {
  customer: Customer | null;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
}

export const CustomerEditDialog: React.FC<CustomerEditDialogProps> = ({
  customer,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const customerService = CustomerService.getInstance();
  const [formData, setFormData] = useState<Customer | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const displayedCustomer = customer && (formData?.id === customer.id ? formData : customer);

  const closeDialog = () => {
    setDialogError(null);
    setNameError(false);
    setFormData(null);
    onClose();
  };

  const handleSave = async () => {
    if (!displayedCustomer) return;
    setDialogError(null);
    const hasNameError = !displayedCustomer.name.trim();
    setNameError(hasNameError);
    if (hasNameError) {
      nameInputRef.current?.focus();
      return;
    }

    setIsSaving(true);
    try {
      const response = await customerService.editCustomer({
        ...displayedCustomer,
        name: displayedCustomer.name.trim(),
      });
      if (response.success && response.data) {
        onSuccess(response.data);
        closeDialog();
      } else {
        setDialogError(
          response.error || t("management.customers.editCustomer.errors.failedUpdateCustomer"),
        );
      }
    } catch (error) {
      setDialogError(
        error instanceof Error
          ? error.message
          : t("management.customers.editCustomer.errors.errorUpdatingCustomer"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updateCustomer = (updates: Partial<Customer>) => {
    if (!displayedCustomer) return;
    setFormData({ ...displayedCustomer, ...updates });
  };

  return (
    <AdminDialog
      open={Boolean(displayedCustomer)}
      onClose={closeDialog}
      maxWidth="sm"
      title={t("management.customers.editCustomer.title")}
      icon={<CustomersIcon />}
      error={dialogError}
      errorOperation="update"
      errorEntity={t("common.entities.customer")}
      cancelAction={{ label: t("common.cancel"), onClick: closeDialog, disabled: isSaving }}
      primaryAction={{
        label: isSaving
          ? t("management.customers.editCustomer.actions.saving")
          : t("management.customers.editCustomer.actions.save"),
        onClick: () => void handleSave(),
        loading: isSaving,
      }}
    >
      {displayedCustomer && (
        <>
          <TextField
            fullWidth
            required
            margin="normal"
            label={t("management.customers.customers.fields.customerName")}
            value={displayedCustomer.name}
            inputRef={nameInputRef}
            error={nameError}
            helperText={nameError ? t("common.required") : undefined}
            disabled={isSaving}
            onChange={(event) => {
              updateCustomer({ name: event.target.value });
              if (event.target.value.trim()) setNameError(false);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("management.customers.customers.fields.initial")}
            value={displayedCustomer.initial ?? ""}
            inputProps={{ maxLength: 10 }}
            disabled={isSaving}
            onChange={(event) => updateCustomer({ initial: event.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("management.customers.customers.fields.saveForLaterTemplate")}
            value={displayedCustomer.saveForLaterTemplate}
            disabled={isSaving}
            onChange={(event) => updateCustomer({ saveForLaterTemplate: event.target.value })}
          />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={displayedCustomer.live}
                    disabled={isSaving}
                    onChange={(event) => updateCustomer({ live: event.target.checked })}
                  />
                }
                label={t("management.customers.customers.fields.live")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("management.customers.customers.hints.live")}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={displayedCustomer.active ?? true}
                    disabled={isSaving}
                    onChange={(event) => updateCustomer({ active: event.target.checked })}
                  />
                }
                label={t("management.customers.customers.fields.active")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("management.customers.customers.hints.active")}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </AdminDialog>
  );
};
